// Password reset / set-password links - public (token-gated) API calls
//
// Backed by /api/auth/password-reset (app/routes/password_reset.py). No
// session is needed: the token in the emailed link is the credential.

import api from './api';

export interface ResetRequestResponse {
  message: string;
}

export interface ValidateLinkResponse {
  valid: boolean;
  reason?: string | null;
  message?: string | null;
  email?: string | null;
  name?: string | null;
  purpose?: 'set_password' | 'reset' | string | null;
  expires_at?: string | null;
}

export interface ConfirmResetResponse {
  message: string;
  email: string;
}

/** The {code, message} the confirm endpoint answers a refusal with. */
export interface ResetRefusal {
  code: string;
  message: string;
}

export const passwordResetService = {
  /** "Forgot your password?": always resolves the same message. */
  request: async (email: string): Promise<ResetRequestResponse> => {
    const response = await api.post<ResetRequestResponse>('/auth/password-reset/request', {
      email: email.trim().toLowerCase(),
    });
    return response.data;
  },

  /** Is this link still good, and for whom? */
  validate: async (token: string): Promise<ValidateLinkResponse> => {
    const response = await api.get<ValidateLinkResponse>('/auth/password-reset/validate', {
      params: { token },
    });
    return response.data;
  },

  /** Redeem the link with the chosen password. */
  confirm: async (token: string, newPassword: string): Promise<ConfirmResetResponse> => {
    const response = await api.post<ConfirmResetResponse>('/auth/password-reset/confirm', {
      token,
      new_password: newPassword,
    });
    return response.data;
  },
};

/** Pull the refusal out of an axios error, or null when it is not one. */
export function resetRefusalFrom(err: unknown): ResetRefusal | null {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (detail && typeof detail === 'object' && 'code' in detail && 'message' in detail) {
    const d = detail as { code: unknown; message: unknown };
    if (typeof d.code === 'string' && typeof d.message === 'string') {
      return { code: d.code, message: d.message };
    }
  }
  return null;
}
